<template>
	<teleport to="body">
		<div v-if="show" :data-theme="currentTheme" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50">
			<div class="popup-bg text-[var(--popup-text-color)] rounded-lg p-8 w-11/12 md:w-4/5 lg:w-3/5 relative brightness-110
			max-h-[90vh] overflow-y-auto md:max-h-none md:overflow-visible">
				<button @click="$emit('close')" class="absolute top-4 right-4">
					<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close Icon" class="w-3 h-3"/>
				</button>

				<h2 class="text-2xl font-archivomedium text-center my-4">
					Transaction Details
				</h2>
				<p class="text-center font-archivoregular mb-6">
					View the details of the transaction below
				</p>

				<!-- Bitcoin-specific layout -->
				<div v-if="selectedChain.name === 'Bitcoin'">
					<div class="flex justify-center mb-6">
						<span
						:class="{
						'bg-[#9FFFCA] text-[#00671D]': tx.status === 'Confirmed',
						'bg-[#FFD5AE] text-[#EC5900]': tx.status === 'Pending',
						'bg-[#E38B8B] text-[#B50000]': tx.status === 'Rejected'
						}"
						class="px-3 py-1 text-sm rounded-full font-archivomedium uppercase"
						>
							{{ tx.status }}
						</span>
					</div>

					<!-- Inputs → Outputs -->
					<div class="flex flex-col space-y-2 md:flex-row items-start md:items-center my-8 p-6 popup-bg-2 border border-[var(--accent-color)] rounded-lg">
						<!-- Inputs -->
						<div class="md:w-1/2 space-y-2 text-xs text-[var(--btc-tx-inputs-colour)]">
							<h3 class="font-archivomedium text-lg mb-2">Inputs</h3>
							<ul class="space-y-3">
								<li v-for="(inp, i) in tx.inputsArr" :key="i" class="break-all text-sm">
								{{ inp }}
								</li>
							</ul>
						</div>

						<!-- Mobile Arrow -->
						<div class="flex md:hidden justify-center mx-auto my-2">
							<svg class="w-6 h-6 text-[var(--popup-text-color)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
							</svg>
						</div>

						<!-- Arrow -->
						<div class="hidden md:flex mx-4">
							<svg class="w-6 h-6 text-[var(--popup-text-color)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
							</svg>
						</div>

						<!-- Outputs + amounts -->
						<div class="md:w-1/2 space-y-3 text-xs text-[var(--btc-tx-amount-colour)]">
							<h3 class="font-archivomedium text-lg mb-2">Outputs</h3>
							<ul class="space-y-3">
								<li v-for="(out, i) in tx.outputsArr" :key="i" class="flex justify-between space-x-4 items-start text-sm">
									<span class="break-all">{{ out }}</span>
									<span class="font-archivosemibold">{{ tx.outputValuesArr[i] }}</span>
								</li>
							</ul>
						</div>
					</div>
					<div class="mt-8">
						<div class="flex justify-between text-xs text-[var(--evm-tx-block-colour)] md:hidden">
							<div class="space-y-2">
								<div><span class="font-archivomedium text-[var(--accent-color)] pr-2">Date:</span> {{ tx.date }}</div>
								<div><span class="font-archivomedium text-[var(--accent-color)] pr-2">Block:</span> {{ tx.block }}</div>
							</div>
							<div class="space-y-2 text-right">
								<div><span class="font-archivomedium text-[var(--accent-color)] pl-2">Confirmations:</span> {{ tx.confirmations }}</div>
								<div><span class="font-archivomedium text-[var(--accent-color)] pl-2">Fees:</span> {{ tx.fees }}</div>
							</div>
						</div>
						<!-- desktop -->
						<div class="hidden md:grid grid-cols-4 gap-4 text-xs font-archivomedium uppercase text-[var(--accent-color)]">
							<div class="text-center">Date</div>
							<div class="text-center">Block</div>
							<div class="text-center">Confirmations</div>
							<div class="text-center">Fees</div>
						</div>
							<div class="hidden md:grid grid-cols-4 gap-4 mt-2 text-sm text-[var(--popup-text-color)]">
							<div class="text-center">{{ tx.date }}</div>
							<div class="text-center">{{ tx.block }}</div>
							<div class="text-center">{{ tx.confirmations }}</div>
							<div class="text-center">{{ tx.fees }}</div>
						</div>
					</div>
				</div>


				<!-- evm -->
				<div v-else>
					<div class="flex justify-center mb-4">
						<span
						:class="{
						'bg-[var(--new-bg-received)] text-[var(--new-text-received)]': tx.type === 'Received',
						'bg-[var(--new-bg-sent)] text-[var(--new-text-sent)]': tx.type === 'Sent',
						'bg-[var(--new-bg-swap)] text-[var(--new-text-swap)]': isSwap
						}"
						class="px-3 py-1 text-sm rounded-full font-archivomedium uppercase"
						>
							{{ isSwap ? 'Smart Contract' : tx.type }}
						</span>
					</div>

					<div v-if="!isSwap" class="flex justify-center mb-6">
						<span class="text-lg font-archivoregular text-[var(--evm-tx-amount-colour)]">
							{{ tx.amount }}
						</span>
					</div>

					<div class="flex flex-col md:flex-row items-start md:items-center my-8 p-6 popup-bg-2 border border-[var(--accent-color)] rounded-lg">
						<div class="basis-[65%] space-y-4">
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">From:</span>
								<span class="break-all px-1 text-xs md:text-sm text-[var(--evm-tx-popup-text-colour)]">{{ tx.from }}</span>
							</p>
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">
								{{ isSwap ? 'Interacted With:' : 'To:' }}
								</span>
								<span class="break-all px-1 text-xs md:text-sm text-[var(--evm-tx-popup-text-colour)]">{{ tx.to }}</span>
							</p>
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">
								Hash:
								</span>
								<span class="break-all px-1 text-xs md:text-sm text-[var(--evm-tx-popup-text-colour)]">{{ tx.hash }}</span>
							</p>
						</div>

						<div class="hidden md:block divider-vertical"></div>

						<div class="basis-[35%] space-y-4 mt-4 md:mt-0">
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">Block:</span>
								<span class="px-1 py-1 text-xs md:text-sm text-[var(--evm-tx-block-colour)]">{{ tx.block }}</span>
							</p>
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">Date:</span>
								<span class="px-1 py-1 text-xs md:text-sm text-[var(--evm-tx-block-colour)]">{{ tx.date }}</span>
							</p>
							<p>
								<span class="font-archivomedium text-[var(--evm-tx-to-colour)]">Confirmations:</span>
								<span class="px-1 py-1 text-xs md:text-sm text-[var(--evm-tx-block-colour)]">{{ tx.confirmations }}</span>
							</p>
						</div>
					</div>
				</div>

				<!-- explorere -->
				<div class="flex justify-center mt-6">
					<a :href="`${explorerUrl}/tx/${tx.hash}`" target="_blank" class="px-4 py-2 bg-[var(--accent-color)] text-[var(--drop-popup-text-color)] border border-[var(--accent-color)] hover:bg-transparent hover:text-[var(--accent-color)] rounded-full font-archivoregular transition duration-200">
						View On Explorer
					</a>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script setup>
	import { computed } from 'vue';
	import lightX from '../assets/X.png';
	import darkX from '../assets/XWhite.png';

	const props = defineProps({
		show: Boolean,
		tx: Object,
		explorerUrl: String,
		currentTheme: String,
		selectedChain: { type: Object, required: true },
	});

	const emit = defineEmits(['close']);

	const isSwap = computed(() => {
		if (props.selectedChain.name === 'Bitcoin') return false;
		return props.tx.amount.trim() === '0 ETH';
	});
</script>

<style scoped>
	.popup-bg {
		background: var(--popup-bg-color);
	}

	.popup-bg-2 {
		background: var(--popup-bg-2-color);
	}
	/* Vertical divider with fade top & bottom */
	.divider-vertical {
		width: 1.5px;
		margin: 0 16px;
		background: linear-gradient(to bottom, transparent, var(--border-color), transparent);
		/* match the height of your two-column content */
		height: 10rem;
	}

</style>